import type { PDFDocumentProxy, PDFPageProxy, RenderTask } from 'pdfjs-dist/legacy/build/pdf.mjs';
import { pdfRectToViewport, type PdfRect, type PdfViewport } from '../../shared/pdf/geometry';
import { PDF_LIMITS } from '../../shared/pdf/limits';

export interface PdfPreviewLimits { retainedCanvases: number; canvasPixels: number; pixelRatio: number }
export const PDF_PREVIEW_LIMITS: PdfPreviewLimits = Object.freeze({ retainedCanvases: 2, canvasPixels: 4_000_000, pixelRatio: 2 });
export type PdfPageRenderResult = { status: 'cancelled' } | { status: 'rendered'; viewport: PdfViewport; pixelRatio: number };
interface Request { cancelled: boolean; task?: RenderTask; work: Promise<PdfPageRenderResult> }

/** CSS overlay geometry never uses canvas backing-pixel dimensions. */
export function placementOverlayStyle(viewport: PdfViewport, rect: PdfRect) {
  const box = pdfRectToViewport(viewport, rect);
  return { position: 'absolute' as const, left: `${box.x}px`, top: `${box.y}px`, width: `${box.width}px`, height: `${box.height}px` };
}
export class PdfPreview {
  private requests = new Map<HTMLCanvasElement, Request>();
  private disposed = false;
  readonly pageCount: number;
  constructor(private readonly pdf: PDFDocumentProxy, private readonly destroyLoadingTask: () => Promise<void>, private readonly limits: PdfPreviewLimits = PDF_PREVIEW_LIMITS) {
    if (![limits.retainedCanvases, limits.canvasPixels].every(value => Number.isSafeInteger(value) && value > 0) || !Number.isFinite(limits.pixelRatio) || limits.pixelRatio <= 0) throw new Error('Invalid preview limits');
    if (!Number.isSafeInteger(pdf.numPages) || pdf.numPages < 1) throw new Error('Unsupported PDF page count');
    this.pageCount = pdf.numPages;
  }
  async viewport(pageIndex: number, scale: number): Promise<PdfViewport> {
    this.check(pageIndex, scale);
    const page = await this.pdf.getPage(pageIndex + 1), viewport = page.getViewport({ scale });
    if (this.disposed) throw new Error('PDF preview is closed');
    return { width: viewport.width, height: viewport.height, transform: viewport.transform as [number, number, number, number, number, number] };
  }
  async pageText(pageIndex: number): Promise<string> {
    this.check(pageIndex, 1);
    const page = await this.pdf.getPage(pageIndex + 1), content = await page.getTextContent();
    if (this.disposed) return '';
    let text = '';
    for (const item of content.items) if ('str' in item) {
      text += item.str + (item.hasEOL ? '\n' : ' ');
      if (text.length >= 10000) break;
    }
    return text.slice(0, 10000).trim();
  }
  private check(pageIndex: number, scale: number) {
    if (this.disposed) throw new Error('PDF preview is closed');
    if (!Number.isSafeInteger(pageIndex) || pageIndex < 0 || pageIndex >= this.pageCount || !Number.isFinite(scale) || scale < .1 || scale > 4) throw new Error('Invalid PDF page or preview scale');
  }
  renderPage(canvas: HTMLCanvasElement, pageIndex: number, scale = 1, requestedRatio = globalThis.devicePixelRatio || 1): Promise<PdfPageRenderResult> {
    this.check(pageIndex, scale);
    if (!Number.isFinite(requestedRatio) || requestedRatio <= 0) throw new Error('Invalid preview pixel ratio');
    const prior = this.requests.get(canvas); if (prior) { prior.cancelled = true; prior.task?.cancel(); }
    let previousWork = prior?.work;
    if (!prior && this.requests.size >= this.limits.retainedCanvases) {
      previousWork = this.release(this.requests.keys().next().value!).then(() => ({ status: 'cancelled' as const }));
    }
    const request: Request = { cancelled: false, work: Promise.resolve({ status: 'cancelled' }) };
    this.requests.set(canvas, request);
    request.work = this.perform(canvas, pageIndex, scale, requestedRatio, request, previousWork);
    return request.work;
  }
  private async perform(canvas: HTMLCanvasElement, pageIndex: number, scale: number, requestedRatio: number, request: Request, prior?: Promise<PdfPageRenderResult>): Promise<PdfPageRenderResult> {
    let page: PDFPageProxy | undefined;
    const current = () => !this.disposed && !request.cancelled && this.requests.get(canvas) === request;
    try {
      await prior?.catch(() => {}); // A canvas cannot be reused while its old task is painting.
      if (!current()) return { status: 'cancelled' };
      page = await this.pdf.getPage(pageIndex + 1);
      if (!current()) return { status: 'cancelled' };
      const viewport = page.getViewport({ scale });
      if (![viewport.width, viewport.height].every(value => Number.isFinite(value) && value > 0 && value / scale <= PDF_LIMITS.pagePoints)) throw new Error('Unsupported PDF page dimensions');
      // The one-pixel minimum on a narrow dimension must not defeat the pixel cap.
      const ratio = Math.min(requestedRatio, this.limits.pixelRatio, Math.sqrt(this.limits.canvasPixels / (viewport.width * viewport.height)), this.limits.canvasPixels / viewport.width, this.limits.canvasPixels / viewport.height);
      if (!Number.isFinite(ratio) || ratio <= 0) throw new Error('Invalid PDF viewport');
      canvas.width = Math.max(1, Math.floor(viewport.width * ratio)); canvas.height = Math.max(1, Math.floor(viewport.height * ratio));
      canvas.style.width = `${viewport.width}px`; canvas.style.height = `${viewport.height}px`;
      request.task = page.render({ canvas, viewport, transform: ratio === 1 ? undefined : [ratio, 0, 0, ratio, 0, 0] });
      await request.task.promise;
      return current() ? { status: 'rendered', viewport: { width: viewport.width, height: viewport.height, transform: viewport.transform as [number, number, number, number, number, number] }, pixelRatio: ratio } : { status: 'cancelled' };
    } catch (error) {
      if (!current() || (error instanceof Error && error.name === 'RenderingCancelledException')) return { status: 'cancelled' };
      throw new Error(`Could not render PDF page ${pageIndex + 1}.`, { cause: error });
    } finally { page?.cleanup(); }
  }
  async release(canvas: HTMLCanvasElement): Promise<void> {
    const request = this.requests.get(canvas);
    this.requests.delete(canvas);
    if (request) { request.cancelled = true; request.task?.cancel(); await request.work.catch(() => {}); }
    if (!this.requests.has(canvas)) { canvas.width = 0; canvas.height = 0; }
  }
  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    const canvases = [...this.requests.keys()];
    const releases = canvases.map(canvas => this.release(canvas));
    await this.destroyLoadingTask();
    await Promise.all(releases);
  }
}
/**
 * pdf.js reads a page's text with `for await` over a ReadableStream, which Safari before 27 can't iterate ("undefined is
 * not a function"). This adds the standard iteration where it is missing.
 */
export function iterableReadableStreams(): void {
  const proto = globalThis.ReadableStream?.prototype as (ReadableStream & { [Symbol.asyncIterator]?: unknown }) | undefined;
  if (!proto || typeof proto[Symbol.asyncIterator] === 'function') return;
  async function* values<R>(this: ReadableStream<R>, { preventCancel = false } = {}): AsyncGenerator<R, void, undefined> {
    const reader = this.getReader();
    let finished = false;
    try {
      for (;;) { const { done, value } = await reader.read(); if (done) { finished = true; return; } yield value; }
    } finally {
      if (!finished && !preventCancel) await reader.cancel().catch(() => {});
      reader.releaseLock();
    }
  }
  for (const key of ['values', Symbol.asyncIterator]) Object.defineProperty(proto, key, { value: values, writable: true, configurable: true });
}
export async function openPdfPreview(bytes: Uint8Array): Promise<PdfPreview> {
  iterableReadableStreams();
  if (!(bytes instanceof Uint8Array) || bytes.length > PDF_LIMITS.outputBytes) throw new Error('PDF preview exceeds the 8 MB limit');
  const pdfjs = await (await import('./pdfjs')).loadPdfjs();
  const loading = pdfjs.getDocument({ data: bytes.slice(), enableXfa: false, stopAtErrors: true, maxImageSize: 4_000_000 });
  try { return new PdfPreview(await loading.promise, () => loading.destroy()); }
  catch (error) { await loading.destroy(); throw new Error('Could not open this PDF preview.', { cause: error }); }
}
